import {useState, useEffect} from 'react';
import {DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors, closestCenter} from '@dnd-kit/core';
import {arrayMove, sortableKeyboardCoordinates} from '@dnd-kit/sortable';
import {ImageType} from '../model';

interface UseDragAndDropProps {
  imageType: ImageType;
  images: {fileName: string; url: string}[];
  fundraiserId: string;
  updateMainImagesOrder: (images: string[]) => void;
  updateFeedImagesOrder: (images: string[]) => void;
}

export const useDragAndDrop = ({
  imageType,
  images,
  fundraiserId,
  updateMainImagesOrder,
  updateFeedImagesOrder,
}: UseDragAndDropProps) => {
  const [localImagesOrder, setLocalImagesOrder] = useState<{fileName: string; url: string}[]>([]);

  // Сенсоры для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Сбрасываем локальное состояние при смене типа изображений или ID фандрайзера
  useEffect(() => {
    setLocalImagesOrder([]);
  }, [imageType, fundraiserId]);

  // Получение актуального списка изображений
  const getCurrentImages = () => {
    return localImagesOrder.length > 0 ? localImagesOrder : images;
  };

  // Обработчик завершения перетаскивания
  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (!over || active.id === over.id || imageType === ImageType.AVATAR) {
      return;
    }

    const currentImages = getCurrentImages();
    const oldIndex = currentImages.findIndex((img) => img.fileName === active.id);
    const newIndex = currentImages.findIndex((img) => img.fileName === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(currentImages, oldIndex, newIndex);
      const fileNamesOrder = newOrder.map((img) => img.fileName);

      // Мгновенно обновляем UI (оптимистичное обновление)
      setLocalImagesOrder(newOrder);

      // Отправляем на сервер
      switch (imageType) {
        case ImageType.MAIN:
          updateMainImagesOrder(fileNamesOrder);
          break;
        case ImageType.FEED:
          updateFeedImagesOrder(fileNamesOrder);
          break;
      }
    }
  };

  const isDragDisabled = imageType === ImageType.AVATAR || images.length <= 1;

  return {
    sensors,
    currentImages: getCurrentImages(),
    handleDragEnd,
    isDragDisabled,
    collisionDetection: closestCenter,
  };
};
